import { useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';

/**
 * True only while `ref` is on screen AND the tab is visible. Continuous
 * animations (diamond travel, glow pulses) key their play state off this so
 * off-viewport and hidden-tab sections cost zero GPU/CPU — part of the
 * motion performance budget.
 */
export function useInViewActive<T extends Element>(ref: RefObject<T | null>): boolean {
  const [inView, setInView] = useState(false);
  const [tabVisible, setTabVisible] = useState(() => document.visibilityState === 'visible');
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    observerRef.current = new IntersectionObserver(
      ([entry]) => setInView(entry?.isIntersecting ?? false),
      { rootMargin: '100px' },
    );
    observerRef.current.observe(el);
    return () => observerRef.current?.disconnect();
  }, [ref]);

  useEffect(() => {
    const onVisibility = () => setTabVisible(document.visibilityState === 'visible');
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  return inView && tabVisible;
}
